// ─────────────────────────────────────────────────────────────
// LoopBuilderInfoView — edits the current loop-builder project's
// ST metadata. Mirrors SongInfoView's writeST/readST contract but
// drops fields irrelevant to loop builder (key, octaves, artist,
// preacher companions, song-side player-type selector).
//
// Opening this view auto-stamps st_playerType = "loopBuilder".
// That way the publish payload picks the loop-builder branch
// without a dropdown the publisher would have to remember.
// ─────────────────────────────────────────────────────────────

import {createElement} from "@opendaw/lib-jsx"
import {Lifecycle} from "@opendaw/lib-std"
import {StudioService} from "@/service/StudioService.ts"
import {STApi} from "@/jerry/st-panel/STApi"
import {STCategory, STProjectMeta} from "@/jerry/st-panel/STTypes"
import {CloudflareConfig} from "@/jerry/cloudflare/CloudflareConfig"
import {ClerkAuth} from "@/jerry/auth/ClerkAuth"

type Construct = {lifecycle: Lifecycle; service: StudioService}

const readST = (service: StudioService): STProjectMeta => {
    if (!service.hasProfile) return {}
    const meta = service.profile.meta as STProjectMeta & Record<string, unknown>
    return {
        st_categories:           meta.st_categories         as STCategory[] | undefined ?? [],
        st_isPreviewable:        meta.st_isPreviewable      as boolean | undefined ?? false,
        st_is_premium:           meta.st_is_premium         as boolean | undefined ?? false,
        st_db_id:                meta.st_db_id              as string | undefined,
        st_status:               (meta.st_status            as STProjectMeta["st_status"]) ?? "draft",
        st_tags:                 meta.st_tags               as string[] | undefined ?? [],
        st_playerType:           "loopBuilder",
        st_loopBuilderSubType:   meta.st_loopBuilderSubType as STProjectMeta["st_loopBuilderSubType"] | undefined ?? "sample",
        st_image_url:            meta.st_image_url          as string | undefined,
        st_image_data:           meta.st_image_data         as string | undefined,
        st_isHidden:             meta.st_isHidden           as boolean | undefined ?? false,
    }
}

const writeST = (service: StudioService, patch: Partial<STProjectMeta>): void => {
    if (!service.hasProfile) return
    for (const [key, value] of Object.entries(patch)) {
        ;(service.profile.meta as Record<string, unknown>)[key] = value
    }
    service.profile.updateModifyDate()
}

const Toggle = ({value, onChange}: {value: boolean; onChange: (next: boolean) => void}) => {
    let current = value
    const thumb: HTMLElement = <div className="st-toggle-thumb"/>
    const el: HTMLElement = (
        <div className={`st-toggle${current ? " on" : ""}`}>
            {thumb}
        </div>
    )
    el.addEventListener("click", () => {
        current = !current
        el.classList.toggle("on", current)
        onChange(current)
    })
    return el
}

type SongStatus = "draft" | "published" | "archived"

const STATUS_OPTIONS: {value: SongStatus; label: string; color: string}[] = [
    {value: "draft",     label: "Draft",     color: "#888888"},
    {value: "published", label: "Published", color: "#4caf50"},
    {value: "archived",  label: "Archived",  color: "#e07c3a"},
]

const StatusPill = ({value, onChange}: {value: SongStatus; onChange: (next: SongStatus) => void}) => {
    let current = value
    const pills = STATUS_OPTIONS.map((opt) => {
        const pill: HTMLElement = (
            <button
                type="button"
                className={`st-status-pill${current === opt.value ? " active" : ""}`}
                style={{
                    "--pill-color": opt.color,
                } as unknown as CSSStyleDeclaration & Record<string, string>}>
                {opt.label}
            </button>
        )
        pill.addEventListener("click", () => {
            current = opt.value
            pills.forEach((p, i) => p.classList.toggle("active", STATUS_OPTIONS[i].value === current))
            onChange(current)
        })
        return pill
    })
    return <div className="st-status-pill-row">{...pills}</div>
}

type ComboItem = {id: string; name: string; color: string}

const Combobox = ({
    placeholder,
    items,
    onSelect,
    onCreate,
}: {
    placeholder: string
    items: () => ComboItem[]
    onSelect: (item: ComboItem) => void
    onCreate: (name: string) => ComboItem
}) => {
    let query = ""
    let dropdownVisible = false
    const input: HTMLInputElement = (
        <input className="st-input" type="text" placeholder={placeholder}/>
    )
    const dropdown: HTMLElement = <div className="st-combo-dropdown" style={{display: "none"}}/>
    const wrap: HTMLElement = (
        <div className="st-combo-wrap">
            {input}
            {dropdown}
        </div>
    )

    const buildDropdown = () => {
        dropdown.innerHTML = ""
        const filtered = items().filter((entry) => entry.name.toLowerCase().includes(query.toLowerCase()))
        for (const entry of filtered) {
            const opt: HTMLElement = <div className="st-combo-option">{entry.name}</div>
            opt.addEventListener("click", () => {
                onSelect(entry)
                input.value = ""
                query = ""
                hideDropdown()
            })
            dropdown.appendChild(opt)
        }
        if (query.trim() && !items().some((entry) => entry.name.toLowerCase() === query.toLowerCase())) {
            const createOpt: HTMLElement = (
                <div className="st-combo-option create">+ Create &quot;{query.trim()}&quot;</div>
            )
            createOpt.addEventListener("click", () => {
                const created = onCreate(query.trim())
                onSelect(created)
                input.value = ""
                query = ""
                hideDropdown()
            })
            dropdown.appendChild(createOpt)
        }
    }

    const showDropdown = () => {
        dropdownVisible = true
        buildDropdown()
        dropdown.style.display = "block"
    }
    const hideDropdown = () => {
        dropdownVisible = false
        dropdown.style.display = "none"
    }

    input.addEventListener("focus", showDropdown)
    input.addEventListener("input", () => {
        query = input.value
        buildDropdown()
        if (!dropdownVisible) showDropdown()
    })
    input.addEventListener("blur", () => setTimeout(hideDropdown, 150))

    return wrap
}

const Badge = ({item, onRemove}: {item: ComboItem; onRemove: () => void}) => {
    const hex = item.color || "#7c5cbf"
    const el: HTMLElement = (
        <span
            className="st-badge"
            style={{
                backgroundColor: `${hex}33`,
                color: hex,
                border: `1px solid ${hex}55`,
            }}>
            {item.name}
            <span className="st-badge-x">×</span>
        </span>
    )
    el.querySelector(".st-badge-x")!.addEventListener("click", onRemove)
    return el
}

export const LoopBuilderInfoView = ({service}: Construct) => {
    if (!service.hasProfile) {
        return (
            <div className="st-section st-empty">
                <div>No project open.</div>
                <div>Open a loop builder project to edit its info.</div>
            </div>
        )
    }

    // Gate: only render the editor when this project is actually a loop
    // builder. Otherwise show a one-click switch to convert it. This is
    // what stops both panels from editing the same project at once.
    const currentType = (service.profile.meta as Record<string, unknown>).st_playerType
    if (currentType !== "loopBuilder") {
        const switchBtn: HTMLElement = (
            <button type="button" className="st-btn primary" style={{marginTop: "12px"}}>
                Switch this project to Loop Builder
            </button>
        )
        switchBtn.addEventListener("click", () => {
            ;(service.profile.meta as Record<string, unknown>).st_playerType = "loopBuilder"
            service.profile.updateModifyDate()
            document.dispatchEvent(new CustomEvent("st:playertype", {detail: "loopBuilder"}))
        })
        return (
            <div className="st-section st-empty">
                <div>This project is a {typeof currentType === "string" ? currentType : "song"}.</div>
                <div>Open Song Info to edit it, or switch the project type.</div>
                {switchBtn}
            </div>
        )
    }

    const st = readST(service)
    const meta = service.profile.meta

    const inputName: HTMLInputElement = (
        <input className="st-input" type="text" placeholder="Loop title" value={meta.name ?? ""}/>
    )
    inputName.addEventListener("blur", () => service.profile.updateMetaData("name", inputName.value))

    let currentCats: STCategory[] = [...(st.st_categories ?? [])]
    const catDisplay: HTMLElement = <div className="st-badge-row"/>

    const refreshCatDisplay = () => {
        catDisplay.innerHTML = ""
        for (const cat of currentCats) {
            catDisplay.appendChild(
                Badge({
                    item: cat,
                    onRemove: () => {
                        currentCats = currentCats.filter((entry) => entry.id !== cat.id)
                        writeST(service, {st_categories: currentCats})
                        refreshCatDisplay()
                    },
                })
            )
        }
    }
    refreshCatDisplay()

    const catCombo = Combobox({
        placeholder: "Search or create Loop Builder category…",
        items: STApi.getLoopBuilderCategories,
        onSelect: (entry) => {
            if (!currentCats.find((existing) => existing.id === entry.id)) {
                currentCats = [...currentCats, entry as STCategory]
                writeST(service, {st_categories: currentCats})
                refreshCatDisplay()
            }
        },
        onCreate: (name) => STApi.createLoopBuilderCategory(name, "#5cb8bf"),
    })

    let isPremium     = st.st_is_premium    ?? false
    let isPreviewable = st.st_isPreviewable ?? false
    let isHidden      = st.st_isHidden      ?? false

    const premiumToggle = Toggle({
        value: isPremium,
        onChange: (next) => { isPremium = next; writeST(service, {st_is_premium: next}) },
    })
    const previewToggle = Toggle({
        value: isPreviewable,
        onChange: (next) => { isPreviewable = next; writeST(service, {st_isPreviewable: next}) },
    })
    const hiddenToggle = Toggle({
        value: isHidden,
        onChange: (next) => { isHidden = next; writeST(service, {st_isHidden: next}) },
    })

    const tagsInput: HTMLInputElement = (
        <input className="st-input" type="text"
               placeholder="Tags (comma separated)"
               value={(st.st_tags ?? []).join(", ")}/>
    )
    tagsInput.addEventListener("blur", () => {
        const tags = tagsInput.value.split(",").map((entry) => entry.trim()).filter(Boolean)
        writeST(service, {st_tags: tags})
    })

    const loopBuilderSubTypeSelect: HTMLSelectElement = (
        <select className="st-input">
            <option value="sample">Sample (Mixdown)</option>
            <option value="factory">Factory (Stems)</option>
        </select>
    )
    loopBuilderSubTypeSelect.value = st.st_loopBuilderSubType ?? "sample"
    loopBuilderSubTypeSelect.addEventListener("change", () => {
        writeST(service, {st_loopBuilderSubType: loopBuilderSubTypeSelect.value as "sample" | "factory"})
    })

    let currentImageUrl:  string | undefined = st.st_image_url
    let currentImageData: string | undefined = st.st_image_data
    let imageUploading = false

    const resizeToDataUrl = (file: File, maxPx = 400): Promise<string> =>
        new Promise((resolve, reject) => {
            const img = new Image()
            const blobUrl = URL.createObjectURL(file)
            img.onload = () => {
                const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
                const canvas = document.createElement("canvas")
                canvas.width  = Math.round(img.width  * scale)
                canvas.height = Math.round(img.height * scale)
                canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height)
                URL.revokeObjectURL(blobUrl)
                resolve(canvas.toDataURL("image/jpeg", 0.82))
            }
            img.onerror = () => { URL.revokeObjectURL(blobUrl); reject(new Error("Image decode failed")) }
            img.src = blobUrl
        })

    const imageDropZone: HTMLElement = <div className="st-img-drop-zone"/>

    const refreshZone = () => {
        imageDropZone.innerHTML = ""
        if (currentImageData || currentImageUrl) {
            imageDropZone.classList.add("has-image")
            const src = currentImageData ?? currentImageUrl!
            const img: HTMLImageElement = <img src={src} className="st-img-actual" alt="Cover"/>
            imageDropZone.appendChild(img)
            const deleteBtn: HTMLElement = (
                <button type="button" className="st-img-delete-btn" title="Remove image">×</button>
            )
            deleteBtn.addEventListener("click", (event) => {
                event.stopPropagation()
                currentImageUrl  = undefined
                currentImageData = undefined
                writeST(service, {st_image_url: undefined, st_image_data: undefined})
                refreshZone()
            })
            imageDropZone.appendChild(deleteBtn)
        } else if (imageUploading) {
            imageDropZone.classList.remove("has-image")
            const lbl: HTMLElement = <div className="st-img-label" style={{color: "var(--st-accent-bright)"}}>Uploading…</div>
            imageDropZone.appendChild(lbl)
        } else {
            imageDropZone.classList.remove("has-image")
            const lbl: HTMLElement = (
                <div className="st-img-label">
                    Drop cover image here<br/>
                    <span style={{opacity: "0.5", fontSize: "0.55rem"}}>or click to browse</span>
                </div>
            )
            imageDropZone.appendChild(lbl)
        }
    }
    refreshZone()

    const uploadImageFile = async (file: File) => {
        if (!file.type.startsWith("image/")) return
        if (imageUploading) return
        imageUploading = true
        refreshZone()
        const ext = file.name.split(".").pop() ?? "jpg"
        const key = `${crypto.randomUUID()}.${ext}`
        const uploadUrl = `${CloudflareConfig.WORKER_URL.replace("/daw/projects", "/daw/audio")}/image-upload?key=${encodeURIComponent(key)}`
        try {
            currentImageData = await resizeToDataUrl(file)
            writeST(service, {st_image_data: currentImageData})
            refreshZone()
            const token = await ClerkAuth.getToken()
            const res = await fetch(uploadUrl, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": file.type,
                },
                body: await file.arrayBuffer(),
            })
            if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
            const data = await res.json() as {image_url: string}
            currentImageUrl = data.image_url
            writeST(service, {st_image_url: currentImageUrl})
        } catch {
            currentImageData = undefined
            writeST(service, {st_image_data: undefined})
            imageDropZone.innerHTML = ""
            const errLabel: HTMLElement = <div className="st-img-label" style={{color: "#e07c3a"}}>Upload failed — try again</div>
            imageDropZone.appendChild(errLabel)
            setTimeout(refreshZone, 2500)
            imageUploading = false
            return
        }
        imageUploading = false
        refreshZone()
    }

    const fileInput: HTMLInputElement = <input type="file" accept="image/*" style={{display: "none"}}/>
    fileInput.addEventListener("change", () => {
        const file = fileInput.files?.[0]
        if (file) uploadImageFile(file)
        fileInput.value = ""
    })
    document.body.appendChild(fileInput)

    imageDropZone.addEventListener("click", () => { if (!currentImageUrl) fileInput.click() })
    imageDropZone.addEventListener("dragover", (event) => { event.preventDefault(); imageDropZone.classList.add("drag-over") })
    imageDropZone.addEventListener("dragleave", () => imageDropZone.classList.remove("drag-over"))
    imageDropZone.addEventListener("drop", (event) => {
        event.preventDefault()
        imageDropZone.classList.remove("drag-over")
        const file = (event as DragEvent).dataTransfer?.files[0]
        if (file) uploadImageFile(file)
    })

    const statusPill = StatusPill({
        value: st.st_status ?? "draft",
        onChange: (next) => writeST(service, {st_status: next}),
    })

    const dbId = st.st_db_id
    const syncStatus: HTMLElement = dbId
        ? <div style={{fontSize: "0.6rem", color: "#80c080"}}>✓ Synced to library (ID: {dbId})</div>
        : <div style={{fontSize: "0.6rem", color: "var(--color-dark)"}}>Not yet published to library</div>

    return (
        <div className="st-section">
            {statusPill}

            <div className="st-field">
                <div className="st-label">Title</div>
                {inputName}
            </div>

            <div className="st-toggle-row">
                <span className="st-toggle-label">Hide from library screen</span>
                {hiddenToggle}
            </div>

            <div className="st-field">
                <div className="st-label">Sub-Type</div>
                {loopBuilderSubTypeSelect}
            </div>

            <hr className="st-divider"/>

            <div className="st-field">
                <div className="st-label">Categories</div>
                {catDisplay}
                {catCombo}
            </div>

            <hr className="st-divider"/>

            <div className="st-field">
                <div className="st-label">Tags</div>
                {tagsInput}
            </div>

            <div className="st-field">
                <div className="st-label">Cover Image</div>
                {imageDropZone}
            </div>

            <hr className="st-divider"/>

            <div className="st-toggle-row">
                <span className="st-toggle-label">Premium</span>
                {premiumToggle}
            </div>

            <div className="st-toggle-row">
                <span className="st-toggle-label">Previewable</span>
                {previewToggle}
            </div>

            <hr className="st-divider"/>

            {syncStatus}
        </div>
    )
}
